import { Culture } from '@/types/culture'
import { Treatment } from '@/types/treatment'

const authenticationRoutes = {
  authUser: '/authentication/auth',
  signup: '/authentication/signup',
  inviteUser: '/authentication/invite',
  signedUrl: '/authentication/signed_url',
  getAccessToken: '/authentication/access_token',
  recoveryUserPass: '/authentication/recover_password',
}

const courrierRoutes = {
  confirmAccount: '/courrier/confirm',
  confirmCompany: '/courrier/confirm',
  askPasswordRecovery: '/courrier/password/change',
}
const userRoutes = {
  updateUser: '/user',
  getMembers: '/users/company',
  getMembersByProject: '/users/project',
  getUserIntegrations: '/user/integrations',
  getUrlIntegration: (integrationName: 'john-deere') =>
    `/user/integration/${integrationName}/authorize`,
  revokeIntegration: (integrationName: 'john-deere') =>
    `/user/integration/${integrationName}/revoke`,
  listOrganizations: (integrationName: 'john-deere') =>
    `/user/integration/${integrationName}/organizations`,
}

const companyRoutes = {
  updateUserByCompany: (id: number, userId: number) =>
    `/company/${id}/enrollment/${userId}`,
  getCompanies: '/companies',
  getFolders: '/companies/folders/get_folders',
  createFolder: '/companies/folders/create_folder',
}

const projectRoutes = {
  createProject: '/project',
  getProjectById: (id: number) => `/project/${id}`,
  editProject: (id: number) => `/project/${id}`,
  updateProjectShare: '/project/share',
  getProjectsByFather: (companyId: number) => `/projects/father/${companyId}`,
  getRegions: (project_id: number) => `/project/${project_id}/regions`,
}

const physiologicalDiagnosisRoutes = {
  getPhysiologicalDiagnoses: (culture: Culture) => `/physiology/${culture}`,
  getPhysiologicalDiagnosisById: (culture: Culture, id: string) =>
    `/physiology/${culture}/${id}`,
  getPhysiologicalDiagnosesMap: (culture: Culture, id: string) =>
    `/physiology/${culture}/${id}/map`,
  getPhysiologicalParams: (culture: Culture) =>
    `/physiology/${culture}/parameters`,
}

const weedRoutes = {
  listDisplays: '/weed/displays',
  listWeedDiagnoses: (culture: string) => `/weed/${culture}`,
  getSummaryInfestation: (culture: string, demandId: string) =>
    `/weed/${culture}/${demandId}/infestation`,
  getSummaryPulverization: (culture: string, demandId: string) =>
    `/weed/${culture}/${demandId}/pulverization`,

  createPulverization: (culture: string, demandId: string) =>
    `/weed/${culture}/${demandId}/pulverization`,
  getURLToDownloadFile: (culture: string, demandId: string, type: string) =>
    `/weed/${culture}/${demandId}/signed/download/${type}`,
  updateIntegrationOnPulverization: (
    culture: string,
    demandId: string,
    integrationType: 'john-deere',
  ) =>
    `/weed/${culture}/${demandId}/pulverization/integration/${integrationType}`,
}

const plantingFailureRoutes = {
  getSummaryPlantingFailure: (culture: string, demandId: string) =>
    `/planting_failure/${culture}/${demandId}/summary`,
  createPlantingFailure: (culture: string, demandId: string) =>
    `/planting_failure/${culture}/${demandId}/create`,
}

const diagnosesRoutes = {
  createDiagnosis: (treatment: Treatment, culture: Culture) =>
    `/${treatment}/${culture}`,
  getDiagnosisById: (
    treatment: Treatment,
    culture: Culture,
    demandId: string,
  ) => `/${treatment}/${culture}/${demandId}`,
  updateDiagnosisById: (demandId: string) => `/demands/${demandId}`,
  getURLToUploadFile: (
    treatment: Treatment,
    culture: Culture,
    demandId: string,
    type: string,
  ) => `/${treatment}/${culture}/${demandId}/signed/upload/${type}`,
  getURLToUploadBatchFiles: (
    treatment: Treatment,
    culture: Culture,
    demandId: string,
    type: string,
  ) => `/${treatment}/${culture}/${demandId}/signed/batch/${type}`,
  confirmDiagnosis: (
    treatment: Treatment,
    culture: Culture,
    diagnosisId: string,
  ) => `/${treatment}/${culture}/${diagnosisId}/confirm`,
  uploadSessionStart: (
    treatment: Treatment | string,
    culture: Culture | string,
    diagnosisId: string,
  ) => `/${treatment}/${culture}/${diagnosisId}/upload-session/start`,
  uploadSessionPart: (
    treatment: Treatment | string,
    culture: Culture | string,
    diagnosisId: string,
  ) => `/${treatment}/${culture}/${diagnosisId}/upload-session/part`,
  uploadSessionFinish: (
    treatment: Treatment | string,
    culture: Culture | string,
    diagnosisId: string,
  ) => `/${treatment}/${culture}/${diagnosisId}/upload-session/finish`,
  getLatestDiagnosisReport: (
    treatment: Treatment,
    culture: Culture,
    diagnosisId: string,
  ) => `/${treatment}/${culture}/${diagnosisId}/reports/latest`,
  reportResolution: (
    treatment: Treatment,
    culture: Culture,
    diagnosisId: string,
    reportId: string,
  ) => `/${treatment}/${culture}/${diagnosisId}/reports/${reportId}/resolution`,
}

const reportsRoutes = {
  getEconomyReports: '/reports/economy',
  getInfestationReports: '/reports/infestation',
  getURLToDownloadReport: (id: string, type: string) =>
    `/reports/${type}/${id}/signed/download`,
  getHerbicideEconomy: '/reports/herbicide-economy',
}

const operationCostsRoutes = {
  getHerbicideTreatments: '/operation-costs/herbicides-treatments',
  deleteHerbicideTreatment: (herbicideTreatmentId: string) =>
    `/operation-costs/herbicides-treatments/${herbicideTreatmentId}`,
  getHerbicides: '/operation-costs/herbicides',
  createHerbicideTreatment: '/operation-costs/herbicides-treatments',
  editHerbicideTreatment: (herbicideTreatmentId: string) =>
    `/operation-costs/herbicides-treatments/${herbicideTreatmentId}`,
}

const calendarRoutes = {
  getActivities: '/calendar/activities',
  getCalendarDiagnoses: '/calendar/diagnoses',
  createMappingActivity: '/calendar/activities',
  createPulverizationActivity: '/calendar/activities/pulverization',
  getPulverizationActivityById: (activityId: string) =>
    `/calendar/activities/${activityId}/pulverization`,
  confirmPulverizationActivity: (activityId: string) =>
    `/calendar/activities/${activityId}/pulverization`,
}

const ibgeRoutes = {
  getStates: '/localidades/estados?orderBy=nome',
  getCities: (uf: string) => `/localidades/estados/${uf}/municipios`,
}

const geoRoutes = {
  checkRegions: '/geo/regions/check',
  createRegions: '/geo/regions',
  getRegionById: (id: number) => `/geo/regions/${id}`,
  getDiagnosesCountByRegionId: (id: number) =>
    `/geo/regions/${id}/fields/demands/count`,
  searchDemandsByRegion: (id: number) => `/geo/regions/${id}/demands/search`,
  getInfestationComparisonByFieldFromRegion: () => '/geo/infestation/compare',
}

export const apiRoutes = {
  ...authenticationRoutes,
  ...courrierRoutes,
  ...userRoutes,
  ...companyRoutes,
  ...projectRoutes,
  ...physiologicalDiagnosisRoutes,
  ...weedRoutes,
  ...plantingFailureRoutes,
  ...diagnosesRoutes,
  ...reportsRoutes,
  ...operationCostsRoutes,
  ...calendarRoutes,
  ...ibgeRoutes,
  ...geoRoutes,
}
